import React, { PureComponent } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {
  Link, Rating,
  ProductPriceBlock, ProductPriceBlockSavePrice
} from '@codex-brightworld/basics';

const ProductTitle = styled.h2`
  margin: 0 0 10px;
  font-weight: normal;
  font-size: 14px;
`;

const ProductContent = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ProductImageWrapper = styled.div`
  margin-right: 3%;
  width: 35%;
  text-align: center;
`;

const ProductImage = styled.img`
  width: 100%;
  height: auto;

  @media ${props => props.theme.breakpoints.mobile} {
    max-width: 120px;
  }
`;

const ProductPriceWrapper = styled.div`
  width: 62%;
  padding: 10px 3% 0 3%;
  margin-top: ${props => (props.withPriceDrop ? -10 : 0)}px;
  text-align: center;

  ${ProductPriceBlockSavePrice} {
    font-size: 12px;
  }
`;

const ProductRating = styled.div`
  font-size: 12px;
`;

const ProductRatingEmptyMessage = styled.span`
  color: ${props => props.theme.colors.shades[1]};
`;

class ProductBlock extends PureComponent {

  detectPriceDrop() {
    const {
      props: {
        viewPriceDrop,
        product: {
          rawPriceWhenSaved,
          rawPrice
        }
      }
    } = this;

    return viewPriceDrop && !!rawPrice && !!rawPriceWhenSaved &&
      parseFloat(rawPriceWhenSaved) > parseFloat(rawPrice);
  }

  renderProductRating(product) {
    const { props } = this;
    const rating = (
      <Rating
        maxCount={5}
        ratingCount={product.ratingCount}
        ratingValue={product.rating}
      />
    );

    if (!product.ratingCount) {
      return (
        <ProductRating>
          <ProductRatingEmptyMessage>
            {props.noReviewsLabel} (0)
          </ProductRatingEmptyMessage>
        </ProductRating>
      );
    }

    if (props.viewProductLinks && product.productUrl) {
      return (
        <ProductRating>
          <Link
            data-interaction="product-review"
            onClick={props.onLinkClick}
            href={`${product.productUrl}#tab3`}
          >
            {rating}
          </Link>
        </ProductRating>
      );
    }

    return (
      <ProductRating>
        {rating}
      </ProductRating>
    );
  }

  render() {
    const { props } = this;
    const withPriceDrop = this.detectPriceDrop();
    const productImage = props.product.image
      ? <ProductImage alt={props.product.name} src={props.product.image} />
      : null;

    return (
      <div data-component="ProductBlock" data-qa={props.qa}>
        {props.viewTitle && (
          <ProductTitle>
            {props.viewProductLinks && props.product.productUrl
              ? (
                <Link
                  data-interaction="view-product"
                  onClick={props.onLinkClick}
                  href={props.product.productUrl}
                >
                  {props.product.name}
                </Link>
              )
              : props.product.name}
          </ProductTitle>
        )}

        <ProductContent>
          <ProductImageWrapper>
            {props.viewProductLinks && props.product.productUrl
              ? (
                <a
                  data-interaction="view-product"
                  onClick={props.onLinkClick}
                  href={props.product.productUrl}
                >
                  {productImage}
                </a>
              )
              : productImage}
          </ProductImageWrapper>

          <ProductPriceWrapper withPriceDrop={withPriceDrop}>
            <ProductPriceBlock
              bundle={props.product.bundle}
              currentPrice={props.product.price}
              mainProductWasPrice={props.product.wasPrice}
              mainProductWasPriceFrom={props.product.wasPriceFrom}
              mainProductWasPriceTo={props.product.wasPriceTo}
              savingsPrice={props.product.savePrice}
              priceDropLabel={props.priceDropLabel}
              priceDropped={withPriceDrop}
              outOfStock={props.product.isOutOfStock}
            />

            {this.renderProductRating(props.product)}
          </ProductPriceWrapper>
        </ProductContent>
      </div>
    );
  }
}

ProductBlock.propTypes = {
  noReviewsLabel: PropTypes.string,
  priceDropLabel: PropTypes.string,
  product: PropTypes.shape({
    bundle: PropTypes.bool,
    image: PropTypes.string,
    isDownload: PropTypes.bool,
    name: PropTypes.string,
    price: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    rawPrice: PropTypes.number,
    productId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    productUrl: PropTypes.string,
    rating: PropTypes.number,
    ratingCount: PropTypes.number,
    savePrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    wasPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    wasPriceFrom: PropTypes.string,
    wasPriceTo: PropTypes.string,
    priceWhenSaved: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    rawPriceWhenSaved: PropTypes.number,
    isOutOfStock: PropTypes.bool
  }),
  truncateTitle: PropTypes.bool,
  type: PropTypes.string,
  viewProductLinks: PropTypes.bool,
  viewPriceDrop: PropTypes.bool,
  viewTitle: PropTypes.bool,
  onLinkClick: PropTypes.func,
  qa: PropTypes.string
};

ProductBlock.defaultProps = {
  noReviewsLabel: '',
  priceDropLabel: '',
  type: null,
  truncateTitle: false,
  viewProductLinks: false,
  viewPriceDrop: false,
  viewTitle: true,
  onLinkClick: () => {},
  product: {},
  qa: undefined
};

export default ProductBlock;
